<template>
    <div>
        <!-- Floating Button (chat) -->
        <v-btn fab
               dark
               color="primary"
               class="floating-btn cb-fab"
               :style="language=='ar' ? 'left:20px' : 'right:20px'"
               :title="$t('Chat')"
               @click="toggleChat">
            <v-icon size="26">mdi-robot-happy-outline</v-icon>
        </v-btn>

        <!-- Chat panel -->
        <v-card v-if="chatVisible"
                class="cb-panel"
                :class="language=='ar' ? 'cb-left' : 'cb-right'"
                elevation="12"
                :dir="language=='ar' ? 'rtl' : 'ltr'"
                @keydown.esc="toggleChat">
            <!-- header -->
            <div class="cb-head">
                <v-avatar size="34" class="cb-head__avatar">
                    <v-icon color="primary">mdi-robot-happy-outline</v-icon>
                </v-avatar>
                <div class="cb-head__titles">
                    <div class="cb-head__title">{{ $t('Chat') }}</div>
                    <div class="cb-head__status"><span class="cb-dot-online"></span>{{ $t('HowCanIAssistYouToday') }}</div>
                </div>
                <v-spacer />
                <!-- concise toggle: short answers by default, off = full answer -->
                <v-tooltip bottom>
                    <template #activator="{ on }">
                        <div class="cb-concise" v-on="on" @click="concise = !concise">
                            <v-icon small color="white">{{ concise ? 'mdi-text-short' : 'mdi-text-long' }}</v-icon>
                            <span class="cb-concise__lbl">{{ concise ? $t('BriefAnswer') : $t('FullAnswer') }}</span>
                        </div>
                    </template>
                    <span>{{ concise ? $t('BriefAnswerHint') : $t('FullAnswerHint') }}</span>
                </v-tooltip>
                <v-btn icon dark small :title="$t('Close')" @click="toggleChat">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </div>

            <!-- messages -->
            <div ref="body" class="cb-body">
                <div v-for="(message, index) in messages" :key="index"
                     class="cb-row"
                     :class="message.sender === 'user' ? 'cb-row--user' : 'cb-row--bot'">
                    <v-avatar v-if="message.sender === 'bot'" size="26" class="cb-row__avatar">
                        <v-icon small color="primary">mdi-robot-happy-outline</v-icon>
                    </v-avatar>
                    <div class="cb-bubble"
                         :class="[message.sender === 'user' ? 'cb-bubble--user' : 'cb-bubble--bot', { 'cb-bubble--error': message.isError }]"
                         v-html="message.text"></div>
                </div>
                <div v-if="loading" class="cb-row cb-row--bot">
                    <v-avatar size="26" class="cb-row__avatar"><v-icon small color="primary">mdi-robot-happy-outline</v-icon></v-avatar>
                    <div class="cb-bubble cb-bubble--bot cb-typing">
                        <span></span><span></span><span></span>
                    </div>
                </div>
            </div>

            <!-- composer -->
            <div class="cb-foot">
                <v-textarea v-model="newMessage"
                            :placeholder="$t('TypeYourMessage')"
                            rows="1" auto-grow no-resize
                            hide-details dense outlined
                            class="cb-input"
                            :disabled="loading"
                            @keydown.enter.exact.prevent="sendMessage" />
                <v-btn icon color="primary" class="cb-send" :disabled="loading || !newMessage.trim()" @click="sendMessage">
                    <v-icon>{{ language=='ar' ? 'mdi-send mdi-flip-h' : 'mdi-send' }}</v-icon>
                </v-btn>
            </div>
        </v-card>
    </div>
</template>

<script>

    import AiChatService from '@/services/AiChatService'
    import { mapGetters } from 'vuex'
    export default {
        props: {
            value: {
                type: Boolean,
                default: false
            },
        },
        data() {
            return {
                chatVisible: this.value,  // Controls chat visibility
                newMessage: '',
                loading: false,
                // short answers by default; the header toggle flips it to full answers
                concise: true,
                messages: [
                    // isLocal: the opening greeting is ours, not something the model said,
                    // so it is excluded from the history sent to the API
                    { sender: 'bot', text: this.$t('HowCanIAssistYouToday'), isLocal: true }
                ]
            }
        },
        computed: {
            ...mapGetters('user', ['language']),

            getLanguage() {
                return this.language == null ? 'ar' : this.language

            },
        },
        watch: {
            value(newValue) {
                this.chatVisible = newValue // Sync when prop changes
            }
        },
        methods: {
            toggleChat() {
                this.chatVisible = !this.chatVisible  // Toggle chat visibility
                this.$emit('openchat', this.chatVisible)
            },
            async sendMessage() {
                const userMessage = this.newMessage.trim()
                if (!userMessage || this.loading) {
                    return
                }
                // history is built BEFORE pushing the new bubble, so it holds only prior turns
                const history = this.buildHistory()
                this.messages.push({
                    sender: 'user',
                    text: this.escapeHtml(userMessage),
                    raw: userMessage
                })
                this.newMessage = ''
                this.loading = true
                this.scrollToBottom()
                try {
                    // the axios response interceptor already unwraps `.data`, so `res` IS the
                    // ApiResponseDto body: { data: { reply, sessionId }, success, message }
                    const res = await AiChatService.sendMessage(userMessage, history, this.getLanguage, this.concise)
                    const reply = res && res.data ? res.data.reply : null
                    if (res && res.success && reply) {
                        this.messages.push({
                            sender: 'bot',
                            // the reply is plain text (bubbles render v-html) — escape it and
                            // keep the line structure of long RAG answers
                            text: this.escapeHtml(reply).replace(/\n/g, '<br>'),
                            raw: reply
                        })
                    } else {
                        this.pushErrorBubble(res ? res.message : null)
                    }
                } catch (error) {
                    const keyword = error && error.response && error.response.data ? error.response.data.message : null
                    this.pushErrorBubble(keyword)
                } finally {
                    this.loading = false
                    this.scrollToBottom()
                }
            },
            // Prior turns for the API, oldest first: excludes the local greeting and error
            // bubbles (our own translated text, not something the model said). The API is
            // stateless — the transcript rides on every request.
            buildHistory() {
                return this.messages
                    .filter((m) => !m.isLocal && !m.isError)
                    .map((m) => ({
                        role: m.sender === 'bot' ? 'assistant' : 'user',
                        content: m.raw || m.text
                    }))
            },
            pushErrorBubble(keyword) {
                const known = ['RequiredField', 'MaxLengthExceeded', 'AiServiceNotConfigured', 'AiServiceAuthFailed',
                    'AiServiceTimeout', 'AiServiceUnreachable', 'AiServiceBadResponse']
                const key = known.includes(keyword) ? keyword : 'ErrorOccurred'
                this.messages.push({ sender: 'bot', text: this.$t(key), isError: true })
            },
            escapeHtml(text) {
                return String(text)
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
            },
            scrollToBottom() {
                this.$nextTick(() => {
                    const body = this.$refs.body
                    if (body) {
                        body.scrollTop = body.scrollHeight
                    }
                })
            }
        }
    }
</script>

<style scoped>
    /* Floating button (chat) — lower of the two stacked FABs */
    .cb-fab {
        position: fixed;
        bottom: 24px;
        width: 52px !important;
        height: 52px !important;
        box-shadow: 0 4px 14px rgba(27,131,84,.4) !important;
    }

    /* Panel — cb-* namespace so SurveyAI's styles can't bleed in */
    .cb-panel {
        position: fixed;
        bottom: 100px;
        width: 380px;
        max-width: calc(100vw - 32px);
        height: 540px;
        max-height: calc(100vh - 150px);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        border-radius: 18px !important;
        z-index: 1000000;
    }
    .cb-left { left: 20px; }
    .cb-right { right: 20px; }

    /* Header */
    .cb-head {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px 14px;
        color: #fff;
        background: linear-gradient(135deg, #1B8354 0%, #28a06a 100%);
        flex: 0 0 auto;
    }
    .cb-head__avatar { background: #fff !important; }
    .cb-head__titles { line-height: 1.15; min-width: 0; }
    .cb-head__title { font-weight: 700; font-size: 15px; }
    .cb-head__status { font-size: 11px; opacity: .9; display: flex; align-items: center; gap: 5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 150px; }
    .cb-dot-online { width: 7px; height: 7px; border-radius: 50%; background: #7CFFB2; display: inline-block; box-shadow: 0 0 0 2px rgba(124,255,178,.3); }
    .cb-concise {
        display: flex; align-items: center; gap: 4px; cursor: pointer;
        background: rgba(255,255,255,.16); border-radius: 20px; padding: 3px 10px; user-select: none;
    }
    .cb-concise:hover { background: rgba(255,255,255,.26); }
    .cb-concise__lbl { font-size: 11.5px; color: #fff; font-weight: 600; }

    /* Body — scrollable flex child */
    .cb-body {
        flex: 1 1 0;
        min-height: 0;
        overflow-y: auto;
        overscroll-behavior: contain;
        padding: 14px;
        background: #f4f7f6;
        scrollbar-width: thin;
        display: flex;
        flex-direction: column;
        gap: 10px;
    }
    .cb-body::-webkit-scrollbar { width: 6px; }
    .cb-body::-webkit-scrollbar-thumb { background: #cfdedd; border-radius: 3px; }

    .cb-row { display: flex; align-items: flex-end; gap: 7px; max-width: 100%; }
    .cb-row--user { flex-direction: row-reverse; }
    .cb-row--bot { flex-direction: row; }
    .cb-row__avatar { background: #fff !important; border: 1px solid #e4ecea; flex: 0 0 auto; }

    .cb-bubble {
        max-width: 80%;
        padding: 9px 13px;
        font-size: 14px;
        line-height: 1.55;
        border-radius: 16px;
        white-space: normal !important;
        word-break: break-word;
        box-shadow: 0 1px 2px rgba(0,0,0,.06);
    }
    .cb-bubble--bot { background: #fff; color: #2b3a36; border-bottom-inline-start-radius: 5px; }
    .cb-bubble--user { background: linear-gradient(135deg, #1B8354 0%, #28a06a 100%); color: #fff; border-bottom-inline-end-radius: 5px; }
    .cb-bubble--error { background: #fdecea; color: #c0392b; }

    /* Typing indicator */
    .cb-typing { display: flex; gap: 4px; align-items: center; }
    .cb-typing span { width: 7px; height: 7px; border-radius: 50%; background: #9fb4ae; display: inline-block; animation: cb-bounce 1.2s infinite ease-in-out; }
    .cb-typing span:nth-child(2) { animation-delay: .18s; }
    .cb-typing span:nth-child(3) { animation-delay: .36s; }
    @keyframes cb-bounce { 0%,80%,100% { transform: translateY(0); opacity: .5; } 40% { transform: translateY(-5px); opacity: 1; } }

    /* Composer */
    .cb-foot {
        flex: 0 0 auto;
        display: flex;
        align-items: flex-end;
        gap: 8px;
        padding: 10px 12px;
        background: #fff;
        border-top: 1px solid #eef2f1;
    }
    .cb-input >>> .v-input__control { min-height: 40px; }
    .cb-input >>> fieldset { border-radius: 22px; border-color: #dbe6e3; }
    .cb-input >>> textarea { max-height: 96px; overflow-y: auto; margin-top: 6px; }
    .cb-send { flex: 0 0 auto; }

    @media (max-width: 480px) {
        .cb-panel { left: 8px !important; right: 8px !important; width: auto; bottom: 88px; height: calc(100vh - 150px); }
    }
</style>
